import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Package, Plus, Trash2, Search, Wrench, AlertTriangle, CheckCircle, Calendar, RotateCcw, Clock } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CTMIssuancePage() {
  const { getAuthHeader } = useAuth();
  const [ctmItems, setCtmItems] = useState([]);
  const [ctmIssues, setCtmIssues] = useState([]);
  const [pendingReturns, setPendingReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openIssueDialog, setOpenIssueDialog] = useState(false);
  const [openReceiveDialog, setOpenReceiveDialog] = useState(false);
  const [selectedReturnItem, setSelectedReturnItem] = useState(null);
  const [returnQty, setReturnQty] = useState(0);
  const [returnRemarks, setReturnRemarks] = useState('');
  const [returningItemId, setReturningItemId] = useState(null);
  
  // Issue form state
  const [issueForm, setIssueForm] = useState({
    items: [],
    issued_to: '',
    purpose: '',
    remarks: '',
    is_returnable: false,
    expected_return_date: ''
  });
  
  // Selected item to add
  const [selectedItem, setSelectedItem] = useState(null);
  const [issueQty, setIssueQty] = useState('');
  const [itemPurpose, setItemPurpose] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemsRes, issuesRes, returnsRes] = await Promise.all([
        axios.get(`${API_URL}/api/store/ctm-items`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/store/ctm-issues`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/store/ctm-pending-returns`, { headers: getAuthHeader() })
      ]);
      setCtmItems(itemsRes.data);
      setCtmIssues(issuesRes.data);
      setPendingReturns(returnsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch CTM data:', error);
      toast.error('Failed to load CTM data');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = ctmItems.filter(item => 
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.item_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const addItemToIssue = () => {
    if (!selectedItem) {
      toast.error('Please select an item');
      return;
    }
    
    const qty = parseFloat(issueQty);
    if (!issueQty || isNaN(qty) || qty <= 0) {
      toast.error('Quantity is required');
      return;
    }
    
    if (qty > (selectedItem.bin_card_stock || 0)) {
      toast.error(`Insufficient stock. Available: ${selectedItem.bin_card_stock || 0}`);
      return;
    }
    
    // Check if item already in list
    const existingIdx = issueForm.items.findIndex(i => i.item_id === selectedItem.id);
    if (existingIdx >= 0) {
      const updated = [...issueForm.items];
      updated[existingIdx].quantity += qty;
      setIssueForm({ ...issueForm, items: updated });
    } else {
      setIssueForm({
        ...issueForm,
        items: [...issueForm.items, {
          item_id: selectedItem.id,
          item_name: selectedItem.name,
          item_code: selectedItem.item_code,
          quantity: qty,
          unit: selectedItem.unit || 'NOS',
          purpose: itemPurpose,
          available_stock: selectedItem.bin_card_stock || 0
        }]
      });
    }
    
    // Reset selection
    setSelectedItem(null);
    setIssueQty('');
    setItemPurpose('');
  };

  const removeItemFromIssue = (idx) => {
    const updated = issueForm.items.filter((_, i) => i !== idx);
    setIssueForm({ ...issueForm, items: updated });
  };

  const handleSubmitIssue = async () => {
    if (issueForm.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    if (!issueForm.issued_to) {
      toast.error('Please enter who is receiving the items');
      return;
    }
    
    // Validate expected_return_date when returnable
    if (issueForm.is_returnable && !issueForm.expected_return_date) {
      toast.error('Please select an expected return date for returnable items');
      return;
    }
    
    try {
      // Inject is_returnable and expected_return_date into each item
      const itemsWithReturnInfo = issueForm.items.map(item => ({
        ...item,
        is_returnable: issueForm.is_returnable,
        expected_return_date: issueForm.is_returnable ? issueForm.expected_return_date : null
      }));
      
      const payload = {
        items: itemsWithReturnInfo,
        issued_to_name: issueForm.issued_to,
        purpose: issueForm.purpose,
        remarks: issueForm.remarks
      };
      
      const response = await axios.post(
        `${API_URL}/api/store/ctm-issue`,
        payload,
        { headers: getAuthHeader() }
      );
      
      const successMsg = typeof response.data.message === 'string' 
        ? response.data.message 
        : 'CTM issue created successfully';
      toast.success(successMsg);
      
      if (response.data.warnings?.length > 0) {
        response.data.warnings.forEach(w => {
          const msg = typeof w === 'string' ? w : (w.msg || JSON.stringify(w));
          toast.warning(msg);
        });
      }
      
      setOpenIssueDialog(false);
      setIssueForm({ items: [], issued_to: '', purpose: '', remarks: '', is_returnable: false, expected_return_date: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to create CTM issue:', error);
      const errorDetail = error.response?.data?.detail;
      
      // Handle different error formats
      if (Array.isArray(errorDetail)) {
        // FastAPI Pydantic validation errors - array of {type, loc, msg, input, url}
        errorDetail.forEach(e => {
          const fieldName = e.loc?.slice(1).join('.') || 'field';
          toast.error(`${fieldName}: ${e.msg}`);
        });
      } else if (typeof errorDetail === 'object' && errorDetail.errors) {
        // Custom error format with errors array
        errorDetail.errors.forEach(e => {
          const msg = typeof e === 'string' ? e : e.msg || JSON.stringify(e);
          toast.error(msg);
        });
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Failed to create issue');
      }
    }
  };

  // Handle receiving a CTM return
  const handleReceiveReturn = async () => {
    if (!selectedReturnItem || returnQty <= 0) {
      toast.error('Please enter a valid return quantity');
      return;
    }
    
    const pendingQty = (selectedReturnItem.qty_issued || 0) - (selectedReturnItem.qty_returned || 0);
    if (returnQty > pendingQty) {
      toast.error(`Cannot return more than pending quantity (${pendingQty})`);
      return;
    }
    
    setReturningItemId(selectedReturnItem.issue_id);
    try {
      const response = await axios.post(
        `${API_URL}/api/store/ctm-return/${selectedReturnItem.issue_id}`,
        {
          item_id: selectedReturnItem.item_id,
          quantity_returned: returnQty,
          remarks: returnRemarks
        },
        { headers: getAuthHeader() }
      );
      
      toast.success(response.data.message || 'Return received successfully');
      setOpenReceiveDialog(false);
      setSelectedReturnItem(null);
      setReturnQty(0);
      setReturnRemarks('');
      fetchData();
    } catch (error) {
      console.error('Failed to receive return:', error);
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        errorDetail.forEach(e => toast.error(e.msg || JSON.stringify(e)));
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error('Failed to receive return');
      }
    } finally {
      setReturningItemId(null);
    }
  };

  // Open receive dialog
  const openReceiveReturnDialog = (item) => {
    setSelectedReturnItem(item);
    const pendingQty = (item.qty_issued || 0) - (item.qty_returned || 0);
    setReturnQty(pendingQty); // Default to full pending quantity
    setReturnRemarks('');
    setOpenReceiveDialog(true);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Wrench className="w-6 h-6 text-amber-600" />
              CTM Issuance
            </h1>
            <p className="text-muted-foreground">Issue Consumables, Tools & Machinery items from bin card stock</p>
          </div>
          <Button onClick={() => setOpenIssueDialog(true)} data-testid="new-ctm-issue-btn">
            <Plus className="w-4 h-4 mr-2" />
            New CTM Issue
          </Button>
        </div>

        <Tabs defaultValue="inventory" className="space-y-4">
          <TabsList>
            <TabsTrigger value="inventory">CTM Inventory</TabsTrigger>
            <TabsTrigger value="issues">Issue History</TabsTrigger>
            <TabsTrigger value="returns">
              Pending Returns {pendingReturns.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 px-1.5">{pendingReturns.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* CTM Inventory Tab */}
          <TabsContent value="inventory">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>CTM Items</CardTitle>
                    <CardDescription>Consumables, Tools & Machinery with bin card stock</CardDescription>
                  </div>
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <Input
                      placeholder="Search items..."
                      className="pl-9"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-center py-12">
                    <Wrench className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No CTM items found</p>
                    <p className="text-sm text-muted-foreground mt-1">Add items with category "CTM" in Item Master</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Item Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-center">Unit</TableHead>
                        <TableHead className="text-center">Bin Card Stock</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                              {item.category || 'CTM'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{item.unit || 'NOS'}</TableCell>
                          <TableCell className="text-center">
                            <span className={`font-medium ${(item.bin_card_stock || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {item.bin_card_stock || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={(item.bin_card_stock || 0) <= 0}
                              onClick={() => {
                                setSelectedItem(item);
                                setIssueQty('');
                                setOpenIssueDialog(true);
                              }}
                            >
                              Issue
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Issue History Tab */}
          <TabsContent value="issues">
            <Card>
              <CardHeader>
                <CardTitle>CTM Issue History</CardTitle>
                <CardDescription>Past CTM issuances from bin card stock</CardDescription>
              </CardHeader>
              <CardContent>
                {ctmIssues.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">No CTM issues yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Issue No.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Issued To</TableHead>
                        <TableHead>Purpose</TableHead>
                        <TableHead className="text-center">Items</TableHead>
                        <TableHead>Issued By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ctmIssues.map((issue) => (
                        <TableRow key={issue.id}>
                          <TableCell className="font-mono text-sm font-medium text-amber-700">
                            {issue.issue_no}
                          </TableCell>
                          <TableCell>
                            {issue.issue_date ? format(new Date(issue.issue_date), 'dd/MM/yyyy HH:mm') : '-'}
                          </TableCell>
                          <TableCell>{issue.issued_to || '-'}</TableCell>
                          <TableCell className="max-w-xs truncate">{issue.purpose || '-'}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{issue.total_items || issue.items?.length || 0}</Badge>
                          </TableCell>
                          <TableCell>{issue.issued_by_name || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Returns Tab */}
          <TabsContent value="returns">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <RotateCcw className="w-5 h-5 text-blue-600" />
                      Pending Returns
                    </CardTitle>
                    <CardDescription>Returnable CTM items awaiting return to store</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchData}>
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Clock className="w-8 h-8 mx-auto mb-3 animate-pulse" />
                    <p>Loading...</p>
                  </div>
                ) : pendingReturns.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-500 opacity-50" />
                    <p className="text-muted-foreground">No pending returns</p>
                    <p className="text-sm text-muted-foreground mt-1">All returnable items have been returned</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Issue No.</TableHead>
                        <TableHead>Issue Date</TableHead>
                        <TableHead>Issued To</TableHead>
                        <TableHead>Item Name</TableHead>
                        <TableHead className="text-center">Qty Issued</TableHead>
                        <TableHead className="text-center">Qty Returned</TableHead>
                        <TableHead className="text-center">Qty Pending</TableHead>
                        <TableHead>Expected Return</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingReturns.map((item) => {
                        const pendingQty = (item.qty_issued || 0) - (item.qty_returned || 0);
                        const isOverdue = item.expected_return_date && new Date(item.expected_return_date) < new Date();
                        return (
                          <TableRow key={`${item.issue_id}-${item.item_id}`} className={isOverdue ? 'bg-red-50' : ''}>
                            <TableCell className="font-mono text-sm font-medium text-amber-700">
                              {item.issue_no}
                            </TableCell>
                            <TableCell>
                              {item.issue_date ? format(new Date(item.issue_date), 'dd/MM/yyyy') : '-'}
                            </TableCell>
                            <TableCell>{item.issued_to_name || item.issued_to || '-'}</TableCell>
                            <TableCell className="font-medium">{item.item_name}</TableCell>
                            <TableCell className="text-center">{item.qty_issued}</TableCell>
                            <TableCell className="text-center text-muted-foreground">
                              {item.qty_returned || 0}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant={pendingQty > 0 ? 'destructive' : 'secondary'} className="font-mono">
                                {pendingQty}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {item.expected_return_date ? (
                                <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
                                  {format(new Date(item.expected_return_date), 'dd/MM/yyyy')}
                                  {isOverdue && <AlertTriangle className="w-3 h-3 inline ml-1" />}
                                </span>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openReceiveReturnDialog(item)}
                                disabled={pendingQty <= 0}
                                data-testid={`receive-return-${item.issue_id}`}
                              >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Receive
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* New CTM Issue Dialog */}
        <Dialog open={openIssueDialog} onOpenChange={setOpenIssueDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-600" />
                New CTM Issue
              </DialogTitle>
              <DialogDescription>
                Issue consumables, tools or machinery items from bin card stock
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* Add Item Section */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <h4 className="font-medium mb-3">Add Item to Issue</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-2">
                    <Label>Select CTM Item</Label>
                    <SearchableSelect
                      options={ctmItems
                        .filter(i => (i.bin_card_stock || 0) > 0)
                        .map(item => ({ 
                          value: item.id, 
                          label: `${item.name} (Stock: ${item.bin_card_stock || 0})`,
                          code: item.item_code || '',
                          category: item.category || ''
                        }))
                        .sort((a, b) => a.label.localeCompare(b.label))}
                      value={selectedItem?.id || ''}
                      onValueChange={(value) => {
                        const item = ctmItems.find(i => i.id === value);
                        setSelectedItem(item);
                      }}
                      placeholder="Select item..."
                      searchPlaceholder="Search CTM items..."
                      emptyText="No items with stock found"
                    />
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min="1"
                      max={selectedItem?.bin_card_stock || 999}
                      value={issueQty}
                      onChange={(e) => setIssueQty(e.target.value)}
                    />
                    {selectedItem && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Available: {selectedItem.bin_card_stock || 0} {selectedItem.unit || 'NOS'}
                      </p>
                    )}
                  </div>
                  <div className="flex items-end">
                    <Button onClick={addItemToIssue} disabled={!selectedItem}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              </div>

              {/* Items List */}
              {issueForm.items.length > 0 && (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Item</TableHead>
                        <TableHead className="text-center">Qty</TableHead>
                        <TableHead className="text-center">Available</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {issueForm.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <span className="font-medium">{item.item_name}</span>
                          </TableCell>
                          <TableCell className="text-center font-medium">{item.quantity} {item.unit}</TableCell>
                          <TableCell className="text-center text-muted-foreground">{item.available_stock}</TableCell>
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeItemFromIssue(idx)}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Issue Details */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Issued To *</Label>
                  <Input
                    placeholder="Person/Department name"
                    value={issueForm.issued_to}
                    onChange={(e) => setIssueForm({ ...issueForm, issued_to: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Purpose</Label>
                  <Input
                    placeholder="Reason for issue"
                    value={issueForm.purpose}
                    onChange={(e) => setIssueForm({ ...issueForm, purpose: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  placeholder="Any additional notes"
                  value={issueForm.remarks}
                  onChange={(e) => setIssueForm({ ...issueForm, remarks: e.target.value })}
                />
              </div>

              {/* Returnable Section */}
              <div className="border rounded-lg p-4 bg-blue-50/50">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="is_returnable"
                    checked={issueForm.is_returnable}
                    onCheckedChange={(checked) => setIssueForm({ 
                      ...issueForm, 
                      is_returnable: checked,
                      expected_return_date: checked ? issueForm.expected_return_date : ''
                    })}
                  />
                  <div className="space-y-1">
                    <Label htmlFor="is_returnable" className="cursor-pointer font-medium">
                      Returnable Item(s)
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Check this if items need to be returned after use (e.g., drill bits, measuring tools)
                    </p>
                  </div>
                </div>
                
                {issueForm.is_returnable && (
                  <div className="mt-4 pl-6">
                    <Label>Expected Return Date *</Label>
                    <Input
                      type="date"
                      className="w-48 mt-1"
                      value={issueForm.expected_return_date}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setIssueForm({ ...issueForm, expected_return_date: e.target.value })}
                    />
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenIssueDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitIssue}
                disabled={issueForm.items.length === 0 || !issueForm.issued_to}
                data-testid="submit-ctm-issue-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Create Issue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Receive Return Dialog */}
        <Dialog open={openReceiveDialog} onOpenChange={setOpenReceiveDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-blue-600" />
                Receive CTM Return
              </DialogTitle>
              <DialogDescription>
                Record the return of CTM item(s) to store inventory
              </DialogDescription>
            </DialogHeader>

            {selectedReturnItem && (
              <div className="space-y-4 py-4">
                {/* Item Info */}
                <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-muted-foreground">Issue No:</span>
                      <span className="ml-2 font-mono font-medium">{selectedReturnItem.issue_no}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Issued To:</span>
                      <span className="ml-2">{selectedReturnItem.issued_to_name || selectedReturnItem.issued_to || '-'}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-sm">Item:</span>
                    <span className="ml-2 font-medium">{selectedReturnItem.item_name}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-sm pt-2 border-t">
                    <div>
                      <span className="text-muted-foreground">Issued:</span>
                      <span className="ml-1 font-medium">{selectedReturnItem.qty_issued}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Returned:</span>
                      <span className="ml-1">{selectedReturnItem.qty_returned || 0}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Pending:</span>
                      <Badge variant="destructive" className="ml-1">
                        {(selectedReturnItem.qty_issued || 0) - (selectedReturnItem.qty_returned || 0)}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Return Quantity */}
                <div className="space-y-2">
                  <Label>Quantity to Return *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={(selectedReturnItem.qty_issued || 0) - (selectedReturnItem.qty_returned || 0)}
                    value={returnQty}
                    onChange={(e) => setReturnQty(parseFloat(e.target.value) || 0)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Maximum: {(selectedReturnItem.qty_issued || 0) - (selectedReturnItem.qty_returned || 0)} {selectedReturnItem.unit || 'NOS'}
                  </p>
                </div>

                {/* Remarks */}
                <div className="space-y-2">
                  <Label>Remarks</Label>
                  <Input
                    placeholder="Condition notes, any damage, etc."
                    value={returnRemarks}
                    onChange={(e) => setReturnRemarks(e.target.value)}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenReceiveDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleReceiveReturn}
                disabled={returnQty <= 0 || returningItemId !== null}
                data-testid="confirm-receive-return-btn"
              >
                {returningItemId ? (
                  <>Processing...</>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm Return
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
